import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import AuthTeams from "../../auth/AuthTeams";
import { getApiClient } from "../../redux/api/client";
import { appInsights } from "../../azure/appInsights";
import Loader from "../LoadSpinner";
import { IconButton } from "@fluentui/react/lib/Button";
import {
  Label,
  Stack,
  Sticky,
  StickyPositionType,
  ConstrainMode,
  ScrollablePane,
  ScrollbarVisibility,
} from "@fluentui/react";
import { mergeStyleSets } from "@fluentui/react/lib/Styling";

import { useTheme } from "../../theme/OAS365Theme";
import UserAuth from "../../auth/userAuth";
import "../../css/styles.css";

import {
  DetailsList,
  DetailsListLayoutMode,
} from "@fluentui/react/lib/DetailsList";
import { SelectionMode } from "@fluentui/react/lib/DetailsList";
import { DetailsRowCheck } from "@fluentui/react/lib/DetailsList";
import { DetailsRow } from "@fluentui/react/lib/DetailsList";
import {
  ADV_SEARCH_OBJ_MAX_ROW,
  ADV_RESULTS_STATUS,
  REFRESH_TOKEN_ACTION,
  RESPONSE_TYPE,
  TIME_OUT_STATUS_CODE,
  PROGRESS_STATUS
} from "../../redux/constants/metaData";

import { useCheckin } from "../../redux/hooks/useCheckin";
import { useMetadata } from "../../redux/hooks/useMetadata";
import { useTools } from "../../redux/hooks/useTools";
import { useLogin } from "../../redux/hooks/useLogin";

import {
  FAILED_LOAD_DATA,
  ERROR_TYPE,
  ERROR_DETAILS,
  SERVER_RESPONSE_ERROR,
  UNKOWN_ERROR,
  IFS_SERVER_ERROR,
  NO_LOGICALUNIT_FOUND,
  FAILED_TO_GET_CLASSFORMAT,
  NO_LOGICALUNIT_FOUND_MAIN,
  NO_LOGICALUNIT_FOUND_SUB,
  IFS_TOKEN_EXPIRED,
  ADVANCED_SEARCH_RESULTS_ERROR,
} from "../../redux/constants/messageTypes";
import { useCommonControlsStyle } from "../../theme/styles/CommonControlsStyle";
import advSearchError from "../../assets/advSearchError.svg";
import {
  isTokenRefreshFailed,
  isTokenRefreshRequired,
  getUpdatedTokenRefreshPreAction,
  isValidPlayload,
} from "../../functions/reduxMethod";
import {
  normalizeAdvSearchRow,
  resolveAdvSearchResultRows,
  formatAdvSearchRowDatetimes,
  getAdvSearchRowCellValue,
} from "../../functions/advSearchWhereClause";
import {
  resolveAdvSearchCellValue,
  isAdvSearchKeyFieldColumn,
  getDefaultAdvSearchSortField,
  sortAdvSearchItemsByField,
} from "../../functions/objectConnectionSearchUtils";
import {
  ArrowSortDownLinesFilled,
  ArrowSortUpLinesFilled,
} from "@fluentui/react-icons";

function renderAdvSearchKeyColumnHeader(col, { sortField, sortDescending }) {
  const fieldName = String(col?.fieldName ?? "").trim();
  const isActive =
    sortField &&
    fieldName.toLowerCase() === String(sortField).trim().toLowerCase();
  const SortIcon =
    isActive && !sortDescending
      ? ArrowSortUpLinesFilled
      : ArrowSortDownLinesFilled;

  return (
    <span className="advSearchKeyColumnHeader">
      <span className="advSearchKeyColumnHeaderLabel">{col?.name ?? ""}</span>
      <SortIcon
        aria-hidden
        className="advSearchKeyColumnHeaderSortIcon"
      />
    </span>
  );
}

function renderAdvSearchDataColumn(col) {
  const fieldName = col?.fieldName;
  return {
    ...col,
    onRender: (item) => {
      const text = resolveAdvSearchCellValue(
        getAdvSearchRowCellValue(item, fieldName),
      );
      return <span>{text}</span>;
    },
  };
}

function buildAdvSearchDataColumn(col, { mruKeyNames, advObjConColumns, sortField, sortDescending, onKeyColumnClick }) {
  const base = renderAdvSearchDataColumn(col);
  if (!isAdvSearchKeyFieldColumn(col, mruKeyNames, advObjConColumns)) return base;

  const fieldName = String(col?.fieldName ?? "").trim();
  const isActive =
    sortField &&
    fieldName.toLowerCase() === String(sortField).trim().toLowerCase();

  return {
    ...base,
    isSorted: isActive,
    isSortedDescending: isActive ? sortDescending : true,
    showSortIconWhenUnsorted: false,
    sortAscendingAriaLabel: "Sorted A to Z",
    sortDescendingAriaLabel: "Sorted Z to A",
    onColumnClick: onKeyColumnClick,
    onRenderHeader: () =>
      renderAdvSearchKeyColumnHeader(col, { sortField, sortDescending }),
  };
}

function buildStableRowKey(row) {
  if (row == null || typeof row !== "object") return "";
  // Prefer backend-provided identifiers when available (varies by LU/view)
  const direct =
    row.KEYREF ??
    row.KeyRef ??
    row.keyRef ??
    row.KEY_REF ??
    row.Key_Ref ??
    row.ID ??
    row.Id;
  if (direct != null) {
    const s = String(direct).trim();
    if (s !== "") return s;
  }

  // Fallback: stable composite of row fields (order-independent).
  // This avoids using array index (which changes after search) and keeps Selection stable.
  try {
    // Some LUs (notably work orders) include volatile fields (versions/timestamps)
    // which can change between searches and would break selection persistence.
    const volatileKey = /(^|_)(OBJVERSION|ROWVERSION|ROW_VERSION|VERSION|LASTUPDATED|LAST_UPDATE|UPDATED_AT|MODIFIED_AT|CREATED_AT|CHANGE_DATE|CHANGEDATE|CHANGED_AT|TIMESTAMP|ETAG)($|_)/i;
    const keys = Object.keys(row)
      .filter((k) => k !== "key" && k !== "__rowId" && !volatileKey.test(k))
      .sort();
    return keys
      .map((k) => {
        const v = row[k];
        if (v == null) return `${k}=`;
        if (typeof v === "object") return `${k}=${JSON.stringify(v)}`;
        return `${k}=${String(v)}`;
      })
      .join("|");
  } catch {
    return "";
  }
}

const getKey = (item, index) => {
  // Always use the normalized `item.key` (which we set from a stable identifier).
  if (item?.key != null) {
    const s = String(item.key).trim();
    if (s !== "") return s;
  }
  const fallback = buildStableRowKey(item);
  return fallback || String(index);
};

function normalizeKeyToken(value) {
  if (value == null) return "";
  // Backend rows come from split strings and can include trailing spaces/newlines.
  // For identity keys we remove *all* whitespace to keep it stable across searches.
  return String(value).replace(/\s+/g, "");
}

function buildLuScopedKeyRefKey(row, luName) {
  const backendRowIdRaw = row?.__rowId;
  const backendRowId = normalizeKeyToken(backendRowIdRaw);
  if (backendRowId) return backendRowId;

  const lu = luName?.luName ?? "";
  const keyRefRaw = row?.KEYREF ?? row?.KeyRef ?? row?.keyRef;
  const keyRef = normalizeKeyToken(keyRefRaw);
  const luToken = normalizeKeyToken(lu);
  if (!luToken || !keyRef) return "";
  return `${luToken}|${keyRef}`;
}

function buildMruCompositeKey(row, luName, mruKeys) {
  const lu = luName?.luName ?? "";
  const keys = Array.isArray(mruKeys) ? mruKeys : [];
  if (!row || !lu || keys.length === 0) return "";
  const parts = keys
    .map((k) => {
      const name = (k?.name ?? k?.key ?? "").toString().trim();
      if (!name) return "";
      const v = row[name];
      const vv = v == null ? "" : String(v).trim();
      return `${name}=${vv}`;
    })
    .filter(Boolean);
  if (parts.length === 0) return "";
  return `${lu}|${parts.join("|")}`;
}

/** One canonical id for persistence + re-selection (must match after every search reload). */
function getRowSelectionId(row, index, luName, mruKeys) {
  if (row == null) return "";
  const primary =
    buildLuScopedKeyRefKey(row, luName) ||
    normalizeKeyToken(buildMruCompositeKey(row, luName, mruKeys)) ||
    normalizeKeyToken(buildStableRowKey(row)) ||
    normalizeKeyToken(row?.key != null ? String(row.key) : "") ||
    normalizeKeyToken(String(index));
  return primary;
}

function buildObjConSearchUrl(webUser, luName, whereClause) {
  const params = new URLSearchParams({
    webUser,
    luName,
    whereClause: (whereClause ?? "").trim(),
  });
  return `api/DocumentManagement/get-objconsearchdata?${params.toString()}`;
}

const AdvSearchObjectResults = forwardRef(function AdvSearchObjectResults(
  _props,
  ref,
) {
  const {
    whereClause,
    searchLuName,
    advObjConColumns,
    advObjConSelectedRows,
    mruKeyNames,
    advObjConSearchResult,
  } = useCheckin();
  const { advObjResultsStatus, tokenRefreshPreActionInvoke } = useMetadata();
  const {
    onAdvObjRowSelected,
    onAdvObjResultsStatusChanged,
    onTokenAccessFailed,
    onLoginRefreshPreActionClear,
    onLoginRefreshPreActionLoad,
    onServerErrorMessageInvoke,
    onExceptionMessageInvoke,
    onErrorMessageInvoke,
    onAdvObjConSearchResultChange,
  } = useTools();
  const { onIfsUserLogin } = useLogin();
  const styleTheme = useCommonControlsStyle();
  const theme = useTheme();

  document.documentElement.style.setProperty(
    "--theme-color",
    theme.colors.themeColor,
  );
  document.documentElement.style.setProperty(
    "--theme-font",
    theme.fonts.regular,
  );
  document.documentElement.style.setProperty(
    "--theme-font-size",
    `${theme.fontSizes.menusize}px`,
  );

  const authChannelRef = useRef(new BroadcastChannel("auth"));
  const authChannel = authChannelRef.current;

  const binIcon = { iconName: "Refresh" };

  const bodyHeight = (document.documentElement.offsetHeight / 100) * 80;

  const isDarkMode =
    String(theme.colors.pureColor || "").toLowerCase() === "#000000";
  const scrollbarTrack =
    theme.colors.scrollbarBackground ?? (isDarkMode ? "#525252" : "#fff");
  const scrollbarThumb =
    theme.colors.scrollbarThumb ?? (isDarkMode ? "#848484" : "#ccc");
  const sbWidth = 10;
  const sbHeight = 10;

  const loaderBorderStyle = {
    padding: "10px autopx",
    bottom: "50px",
    marginTop: "20px",
    height: { bodyHeight },
  };
  const messageBorderStyle = {
    margin: "auto",
    width: "200px",
    paddingLeft: "0px",
  };

  const dataLoadingStatus = {
    completed: "Completed",
    loading: "Loading",
    nodata: "NoData",
    error: "Error",
    fail: "Fail",
  };

  const [errorMessage, setErrorMessage] = useState("");
  const [ifsError, setIfsError] = useState("");
  const [items, setItems] = useState([]);
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [sortField, setSortField] = useState("");
  const [sortDescending, setSortDescending] = useState(true);
  const [columnWidths, setColumnWidths] = useState({});
  const [gridRenderKey, setGridRenderKey] = useState(0);

  // Accumulate selected rows across searches so they are not lost
  // Keep selected row identifiers in-memory so we can re-check them after a search reload.
  // Keyed by the backend-provided `__rowId` (fallbacks only when missing).
  const selectedByIdRef = useRef(new Map());
  const itemsRef = useRef([]);
  const searchRequestIdRef = useRef(0);
  const clientFiltersRef = useRef([]);
  const lastUnfilteredRowsRef = useRef([]);
  const advObjConColumnsRef = useRef(advObjConColumns);
  advObjConColumnsRef.current = advObjConColumns;
  const loadSearchResultsRef = useRef(null);
  const whereClauseRef = useRef(whereClause);
  whereClauseRef.current = whereClause;
  const mruKeyNamesRef = useRef(mruKeyNames);
  mruKeyNamesRef.current = mruKeyNames;

  // Keep itemsRef aligned with rendered rows (onSelectionChanged reads this).
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Seed from Redux once (if navigating back into this screen with existing selections).
  useEffect(() => {
    if (selectedByIdRef.current?.size > 0) return;
    const incoming = Array.isArray(advObjConSelectedRows) ? advObjConSelectedRows : [];
    if (incoming.length === 0) return;
    incoming.forEach((r) => {
      const id = normalizeKeyToken(
        String(r?.selectionKey ?? r?.keyRef ?? r?.key ?? ""),
      );
      if (!id) return;
      selectedByIdRef.current.set(id, { ...r, selectionKey: id });
    });
  }, []); // intentionally only once on mount

  // Keep latest callbacks/values in refs
  const searchLuNameRef = useRef(searchLuName);
  searchLuNameRef.current = searchLuName;
  const onAdvObjRowSelectedRef = useRef(onAdvObjRowSelected);
  onAdvObjRowSelectedRef.current = onAdvObjRowSelected;

  const descCol = useMemo(() => {
    const cols = Array.isArray(advObjConColumns) ? advObjConColumns : [];
    return cols.find((c) => {
      const name = String(c?.name ?? "").toLowerCase();
      return name.includes("description") || name === "name" || name === "text";
    });
  }, [advObjConColumns]);


  const identifierCol = useMemo(() => {
    const cols = Array.isArray(advObjConColumns) ? advObjConColumns : [];
    return cols.find((c) =>
      String(c?.name ?? "").toLowerCase().includes("identifier"),
    );
  }, [advObjConColumns]);

  const directiveCol = useMemo(() => {
    const cols = Array.isArray(advObjConColumns) ? advObjConColumns : [];
    return cols.find((c) =>
      String(c?.name ?? "").toLowerCase().includes("directive"),
    );
  }, [advObjConColumns]);

  
  const referenceCol = useMemo(() => {
    const cols = Array.isArray(advObjConColumns) ? advObjConColumns : [];
    return cols.find((c) =>
      String(c?.name ?? "").toLowerCase().includes("reference"),
    );
  }, [advObjConColumns]);

  
  const purposeCol = useMemo(() => {
    const cols = Array.isArray(advObjConColumns) ? advObjConColumns : [];
    return cols.find((c) =>
      String(c?.name ?? "").toLowerCase().includes("purpose"),
    );
  }, [advObjConColumns]);

  const emitSelected = () => {
    onAdvObjRowSelectedRef.current(Array.from(selectedByIdRef.current.values()));
  };

  const toggleRowSelection = (row, index, checked) => {
    const luName = searchLuNameRef.current;
    const id = normalizeKeyToken(
      getRowSelectionId(row, index, luName, mruKeyNamesRef.current),
    );
    if (!id) return;

    if (checked) {
      selectedByIdRef.current.set(id, {
        selectionKey: id,
        keyDesc: row?.KEYDESC,
        keyRef: row?.KEYREF != null ? normalizeKeyToken(row.KEYREF) : row?.KEYREF,
        luName: luName?.luName ?? "",
        nameDesc: luName?.nameDesc ?? "",
        docObjDesc: descCol ? row?.[descCol.fieldName] ?? "" : "",
        docObjIdentifier: identifierCol ? row?.[identifierCol.fieldName] ?? "" : "",
        directive: directiveCol
          ? row?.[directiveCol.fieldName] ?? ""
          : row?.ERR_DESCR ?? "",
        reference: referenceCol
          ? row?.[referenceCol.fieldName] ?? ""
          : row?.REFERENCE ?? "",
        purpose: purposeCol
          ? row?.[purposeCol.fieldName] ?? ""
          : row?.PURPOSE ?? "",
      });
    } else {
      selectedByIdRef.current.delete(id);
    }
    setSelectionVersion((v) => v + 1);
    emitSelected();
  };

  const getRowId = (row, index) => {
    const luName = searchLuNameRef.current;
    return normalizeKeyToken(
      getRowSelectionId(row, index, luName, mruKeyNamesRef.current),
    );
  };

  const toggleAllVisible = (checked) => {
    const data = Array.isArray(itemsRef.current) ? itemsRef.current : [];
    if (data.length === 0) return;

    if (checked) {
      data.forEach((row, idx) => toggleRowSelection(row, idx, true));
      return;
    }

    // Uncheck all visible: remove only those ids that exist in the current grid
    data.forEach((row, idx) => {
      const id = getRowId(row, idx);
      if (id) selectedByIdRef.current.delete(id);
    });
    setSelectionVersion((v) => v + 1);
    emitSelected();
  };

  const resetSortToDefault = useCallback(() => {
    const defaultField = getDefaultAdvSearchSortField(
      mruKeyNamesRef.current,
      advObjConColumnsRef.current,
    );
    setSortField(defaultField);
    setSortDescending(true);
  }, []);

  useEffect(() => {
    resetSortToDefault();
  }, [advObjConColumns, mruKeyNames, resetSortToDefault]);

  const onKeyColumnClick = useCallback(
    (_ev, column) => {
      if (
        !isAdvSearchKeyFieldColumn(
          column,
          mruKeyNamesRef.current,
          advObjConColumnsRef.current,
        )
      ) {
        return;
      }

      const fieldName = String(column?.fieldName ?? "").trim();
      if (!fieldName) return;

      const isSameColumn =
        sortField &&
        fieldName.toLowerCase() === String(sortField).trim().toLowerCase();
      const nextDescending = isSameColumn ? !sortDescending : true;

      setSortField(fieldName);
      setSortDescending(nextDescending);

      const sorted = sortAdvSearchItemsByField(
        itemsRef.current,
        fieldName,
        nextDescending,
      );
      itemsRef.current = sorted;
      setItems(sorted);
    },
    [sortField, sortDescending],
  );

  const onColumnResize = useCallback((column, newWidth) => {
    const key = column?.key;
    if (!key) return;
    setColumnWidths((prev) => ({ ...prev, [key]: newWidth }));
  }, []);

  const columnsWithSelect = useMemo(() => {
    const cols = Array.isArray(advObjConColumns) ? advObjConColumns : [];
    const sortConfig = {
      mruKeyNames,
      advObjConColumns: cols,
      sortField,
      sortDescending,
      onKeyColumnClick,
    };
    return [
      {
        key: "__select",
        name: "",
        fieldName: "__select",
        minWidth: 44,
        maxWidth: 44,
        isResizable: false,
        isIconOnly: true,
        ariaLabel: "Toggle selection",
        onRenderHeader: () => {
          const data = Array.isArray(itemsRef.current) ? itemsRef.current : [];
          const ids = data.map((r, i) => getRowId(r, i)).filter(Boolean);
          const selectedCount = ids.filter((id) => selectedByIdRef.current.has(id)).length;
          const allSelected = ids.length > 0 && selectedCount === ids.length;
          const indeterminate = selectedCount > 0 && selectedCount < ids.length;
          const nextChecked = indeterminate ? true : !allSelected;
          const headerWrapperClass =
            selectedCount > 0
              ? `${classNames.selectAllCheckWrapper} advObjSelectAllWrapper advObjSelectAllWrapperVisible`
              : `${classNames.selectAllCheckWrapper} advObjSelectAllWrapper`;
          return (
            <div className={headerWrapperClass}>
              <DetailsRowCheck
                ariaLabel="Toggle selection for all items"
                canSelect={true}
                isHeader={true}
                isVisible={true}
                selected={allSelected}
                anySelected={selectedCount > 0}
                isIndeterminate={indeterminate}
                onClick={(ev) => {
                  ev?.preventDefault?.();
                  ev?.stopPropagation?.();
                  toggleAllVisible(nextChecked);
                }}
              />
            </div>
          );
        },
        onRender: (item, index) => {
          const id = getRowId(item, index);
          const checked = !!(id && selectedByIdRef.current.has(id));
          const wrapperClass = checked
            ? `${classNames.rowCheckWrapper} advObjRowCheckWrapper advObjRowCheckWrapperSelected`
            : `${classNames.rowCheckWrapper} advObjRowCheckWrapper`;
          return (
            <div className={wrapperClass}>
              <DetailsRowCheck
                ariaLabel="Toggle selection"
                canSelect={true}
                isVisible={true}
                selected={checked}
                anySelected={selectedByIdRef.current.size > 0}
                onClick={(ev) => {
                  ev?.preventDefault?.();
                  ev?.stopPropagation?.();
                  toggleRowSelection(item, index, !checked);
                }}
              />
            </div>
          );
        },
      },
      ...cols.map((col) => {
        const built = buildAdvSearchDataColumn(col, sortConfig);
        const width = columnWidths[col.key];
        return width != null ? { ...built, currentWidth: width } : built;
      }),
    ];
    // selectionVersion forces rerender when selection changes.
  }, [
    advObjConColumns,
    mruKeyNames,
    selectionVersion,
    sortField,
    sortDescending,
    onKeyColumnClick,
    columnWidths,
  ]);

  const scrollSyncRef = useRef(null);
  const resizeRafRef = useRef(0);

  useEffect(() => {
    const handleResize = () => {
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
      }
      resizeRafRef.current = requestAnimationFrame(() => {
        setGridRenderKey((v) => v + 1);
        const root = scrollSyncRef.current;
        if (!root) return;
        const container = root.querySelector(".ms-ScrollablePane--contentContainer");
        const sticky = root.querySelector("[class*='stickyAbove-']");
        if (container && sticky) {
          sticky.scrollLeft = container.scrollLeft;
        }
      });
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeRafRef.current) {
        cancelAnimationFrame(resizeRafRef.current);
      }
    };
  }, []);

  const tableMinWidth = useMemo(() => {
    const cols = Array.isArray(columnsWithSelect) ? columnsWithSelect : [];
    return cols.reduce((sum, col) => sum + (col.minWidth ?? col.maxWidth ?? 100), 0);
  }, [columnsWithSelect]);

  const advSearchGridColors = useMemo(() => {
    const headerBg = theme.colors.themeDarkColor ?? "#4C8D6A";
    const cellBorder = isDarkMode ? "#525252" : "#DCDCDC";
    const rowBg = isDarkMode ? theme.colors.backgroundColor : "#FFFFFF";
    const rowHoverBg = isDarkMode ? "#343434" : "#F5F5F5";
    const selectedBg = isDarkMode
      ? theme.colors.contextMenuHover
      : theme.colors.contextMenuHover ?? "#E2EFDF";
    const headerCellDivider = isDarkMode ? "#3d7a59" : "rgba(255, 255, 255, 0.3)";

    return {
      headerBg,
      headerText: "#FFFFFF",
      cellBorder,
      rowBg,
      rowHoverBg,
      selectedBg,
      headerCellDivider,
    };
  }, [theme, isDarkMode]);

  const advSearchGridTableStyle = useMemo(
    () =>
      mergeStyleSets({
        root: {
          selectors: {
            ".ms-DetailsHeader": {
              backgroundColor: `${advSearchGridColors.headerBg}!important`,
              borderBottom: `1px solid ${advSearchGridColors.cellBorder}`,
              paddingTop: 0,
              height: "auto",
              lineHeight: "normal",
              boxSizing: "border-box",
            },
            ".ms-DetailsHeader-cell": {
              backgroundColor: `${advSearchGridColors.headerBg}!important`,
              borderRight: `1px solid ${advSearchGridColors.headerCellDivider}`,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "flex-start",
            },
            ".ms-DetailsHeader-cell:first-child": {
              borderLeft: `1px solid ${advSearchGridColors.cellBorder}`,
              justifyContent: "center",
            },
            ".ms-DetailsHeader-cellName": {
              color: `${advSearchGridColors.headerText}!important`,
              fontWeight: theme.fontWeight.titleWeight ?? 600,
              fontFamily: theme.fonts.regular,
              fontSize: theme.fontSizes.normal,
              textAlign: "left",
            },
            ".ms-DetailsHeader-cellTitle": {
              backgroundColor: `${advSearchGridColors.headerBg}!important`,
              color: `${advSearchGridColors.headerText}!important`,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              textAlign: "left",
            },
            ".ms-DetailsHeader [class*='cellTooltip-']": {
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              textAlign: "left",
            },
            ".ms-DetailsHeader-cell.is-actionable .ms-DetailsHeader-cellTitle": {
              width: "100%",
            },
            ".ms-DetailsHeader-cell.is-actionable .ms-DetailsHeader-cellName": {
              width: "100%",
            },
            ".advSearchKeyColumnHeader": {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              paddingRight: 6,
            },
            ".advSearchKeyColumnHeaderLabel": {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              flex: "1 1 auto",
              minWidth: 0,
            },
            ".advSearchKeyColumnHeaderSortIcon": {
              fontSize: "13px !important",
              width: "14px !important",
              height: "14px !important",
              flexShrink: 0,
              marginLeft: 4,
              marginTop: -2,
              color: `${advSearchGridColors.headerText}!important`,
            },
            ".ms-DetailsHeader-cellTitle:hover": {
              backgroundColor: `${advSearchGridColors.headerBg}!important`,
              color: `${advSearchGridColors.headerText}!important`,
            },
            ".ms-DetailsHeader-cell.is-actionable:hover": {
              backgroundColor: `${advSearchGridColors.headerBg}!important`,
            },
            ".ms-DetailsHeader-cell.is-sorted .ms-DetailsHeader-cellTitle": {
              backgroundColor: `${advSearchGridColors.headerBg}!important`,
            },
            ".ms-DetailsHeader-cellSizer": {
              backgroundColor: "transparent !important",
              height: 32,
              minHeight: 32,
              cursor: "ew-resize",
              zIndex: 2,
              selectors: {
                ":hover::after": {
                  opacity: "1 !important",
                  background: "rgba(255, 255, 255, 0.85) !important",
                },
                ":focus::after": {
                  opacity: "1 !important",
                  background: "rgba(255, 255, 255, 0.85) !important",
                },
              },
            },
            ".ms-DetailsHeader .ms-FocusZone": {
              backgroundColor: `${advSearchGridColors.headerBg}!important`,
            },
            ".ms-List, .ms-List-page": {
              backgroundColor: `${advSearchGridColors.rowBg}!important`,
            },
            ".ms-DetailsHeader-cell i, .ms-DetailsHeader-cell .ms-Icon": {
              color: `${advSearchGridColors.headerText}!important`,
            },
            ".ms-DetailsHeader-cell [class*='sortIcon']": {
              display: "none !important",
            },
            ".ms-DetailsRow": {
              backgroundColor: `${advSearchGridColors.rowBg}!important`,
            },
            ".ms-DetailsRow:not(.is-selected):hover": {
              backgroundColor: `${advSearchGridColors.rowHoverBg}!important`,
            },
            ".ms-DetailsRow.is-selected, .ms-DetailsRow.is-selected:hover": {
              backgroundColor: `${advSearchGridColors.selectedBg}!important`,
            },
            ".ms-DetailsRow-cell": {
              borderRight: `1px solid ${advSearchGridColors.cellBorder}`,
              borderBottom: `1px solid ${advSearchGridColors.cellBorder}`,
              backgroundColor: "inherit!important",
              color: `${theme.colors.fontColor}!important`,
              fontFamily: theme.fonts.regular,
              fontSize: theme.fontSizes.normal,
            },
            ".ms-DetailsRow-cell:first-child": {
              borderLeft: `1px solid ${advSearchGridColors.cellBorder}`,
            },
            ".ms-Check-check": {
              background: theme.colors.themeDarkColor,
              borderRadius: "50%",
              paddingLeft: "1px",
            },
          },
        },
      }),
    [advSearchGridColors, theme],
  );

  const advSearchDetailListStyle = useMemo(
    () =>
      mergeStyleSets(advSearchGridTableStyle, {
        root: {
          overflowX: "visible",
          overflowY: "visible",
          background: advSearchGridColors.rowBg,
        },
        contentWrapper: {
          minWidth: tableMinWidth,
          background: advSearchGridColors.rowBg,
        },
      }),
    [advSearchGridTableStyle, advSearchGridColors, tableMinWidth],
  );

  const advSearchHeaderStyle = useMemo(
    () =>
      mergeStyleSets(advSearchGridTableStyle, {
        root: {
          minWidth: tableMinWidth,
          background: advSearchGridColors.headerBg,
          paddingTop: 0,
          height: "auto",
          lineHeight: "normal",
          boxSizing: "border-box",
        },
      }),
    [advSearchGridTableStyle, advSearchGridColors.headerBg, tableMinWidth],
  );

  const handleScroll = useCallback((event) => {
    const root = scrollSyncRef.current;
    if (!root) return;
    const sticky = root.querySelector("[class*='stickyAbove-']");
    if (sticky) sticky.scrollLeft = event.target.scrollLeft;
  }, []);

  useEffect(() => {
    if (advObjResultsStatus !== PROGRESS_STATUS.onCompletion) return;
    const root = scrollSyncRef.current;
    if (!root) return;

    const container = root.querySelector(".ms-ScrollablePane--contentContainer");
    if (!container) return;

    const syncHeaderScroll = () => {
      const sticky = root.querySelector("[class*='stickyAbove-']");
      if (sticky) sticky.scrollLeft = container.scrollLeft;
    };

    container.addEventListener("scroll", syncHeaderScroll, { passive: true });
    return () => container.removeEventListener("scroll", syncHeaderScroll);
  }, [advObjResultsStatus, items]);

  const classNames = mergeStyleSets({
    wrapper: {
      position: "relative",
      maxHeight: "inherit",
      overflowY: "hidden",
    },
    detailsList: {
      selectors: {
        ".advObjSelectAllWrapper, .advObjSelectAllWrapperVisible": {
          opacity: 1,
          pointerEvents: "auto",
        },
        ".advObjRowCheckWrapper, .advObjRowCheckWrapperSelected": {
          opacity: 1,
          pointerEvents: "auto",
        },
      },
    },
    rowCheckWrapper: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
    },
    rowCheckWrapperSelected: {},
    selectAllCheckWrapper: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      // Nudge slightly left to match row-cell centering (header has extra padding)
      marginLeft: "-2px",
    },
  });

  useEffect(() => {
    if (advObjResultsStatus === PROGRESS_STATUS.onLoad) {
      loadSearchResults(whereClauseRef.current, undefined, clientFiltersRef.current);
    }
  }, [advObjResultsStatus]);

  // If component remounts (e.g. after aggressive resize/reflow), recover latest rows from Redux cache.
  useEffect(() => {
    if (advObjResultsStatus !== PROGRESS_STATUS.onCompletion) return;
    if (Array.isArray(items) && items.length > 0) return;
    if (!Array.isArray(advObjConSearchResult) || advObjConSearchResult.length === 0) return;
    itemsRef.current = advObjConSearchResult;
    setItems(advObjConSearchResult);
  }, [advObjResultsStatus, items, advObjConSearchResult]);

  useEffect(() => {
    if (triggerRefreshTokenPreAction(tokenRefreshPreActionInvoke) === true) {
      const run = async () => {
        switch (tokenRefreshPreActionInvoke.actionType) {
          case REFRESH_TOKEN_ACTION.advanceSearchObjectsLoad:
            loadSearchResults(whereClause, tokenRefreshPreActionInvoke);
            break;
          default:
            console.log(
              "No action for token refresh pre-action invoke: " +
                tokenRefreshPreActionInvoke.actionType,
            );
            break;
        }
      };
      run();
    }
  }, [tokenRefreshPreActionInvoke]);

  function triggerRefreshTokenPreAction(tokenRefreshPreActionInvoke) {
    if (isValidPlayload(tokenRefreshPreActionInvoke)) {
      return (
        tokenRefreshPreActionInvoke.actionType ===
        REFRESH_TOKEN_ACTION.advanceSearchObjectsLoad
      );
    }

    return false;
  }

  async function loadSearchResults(
    whereClauseStat = "",
    tokenRefreshPreAction = {
      retry: 0,
      actionType: REFRESH_TOKEN_ACTION.advanceSearchObjectsLoad,
      refreshResponse: RESPONSE_TYPE.none,
    },
    clientFilters = [],
  ) {
    const requestId = ++searchRequestIdRef.current;
    clientFiltersRef.current = Array.isArray(clientFilters) ? clientFilters : [];
    setItems([]);
    itemsRef.current = [];
    setIfsError("");
    let appInsightProps = {
      userId: new AuthTeams().getEmail(),
      functionName: "loadSearchResults",
      statusCode: 0,
      selectedRows: null,
    };
    try {
      onAdvObjResultsStatusChanged(PROGRESS_STATUS.onLoading);
      const tokenResponse = await new UserAuth().getUserToken(true);
      const tokenOneDrive = tokenResponse ? tokenResponse.accessToken : null;
      if (!tokenOneDrive) {
        onTokenAccessFailed(true);
        onAdvObjResultsStatusChanged(PROGRESS_STATUS.onError);
        return;
      }

      if (isTokenRefreshFailed(tokenRefreshPreAction)) {
        onLoginRefreshPreActionClear();
        onAdvObjResultsStatusChanged(PROGRESS_STATUS.onError);
        let messageBox = {
          type: ERROR_TYPE,
          message: IFS_TOKEN_EXPIRED,
        };
        setErrorMessage(messageBox.message);
        onErrorMessageInvoke(messageBox);
        return;
      }

      const apiWhereClause =
        whereClauseStat !== undefined && whereClauseStat !== ""
          ? whereClauseStat
          : `WHERE rownum <= ${ADV_SEARCH_OBJ_MAX_ROW}`;

      appInsights.trackTrace({
        message: "Get Logical units list",
        appInsightProps,
      });

      const apiClient = getApiClient(tokenOneDrive);
      const res = await apiClient.get(
        buildObjConSearchUrl(
          new AuthTeams().getEmail(),
          searchLuName.luName,
          apiWhereClause,
        ),
        {
          validateStatus: (status) =>
            (status >= 200 && status < 300) || status === 400,
        },
      );

      if (requestId !== searchRequestIdRef.current) {
        return;
      }

      if (res.status === 200) {
        const mapped = Array.isArray(res.data)
          ? res.data.map((r, idx) => {
              const luNameForRow = searchLuNameRef.current;
              const stableKey =
                normalizeKeyToken(
                  getRowSelectionId(
                    r,
                    idx,
                    luNameForRow,
                    mruKeyNamesRef.current,
                  ),
                ) || String(idx);
              const normalizedRow = normalizeAdvSearchRow(r);
              const formattedRow = formatAdvSearchRowDatetimes(normalizedRow, advObjConColumnsRef.current);
              return { ...formattedRow, key: stableKey };
            })
          : [];
        const columns = advObjConColumnsRef.current;
        const { data: sortedResults, updateSnapshot } = resolveAdvSearchResultRows(
          mapped,
          clientFiltersRef.current,
          columns,
          lastUnfilteredRowsRef.current,
          mruKeyNamesRef.current,
        );
        if (updateSnapshot) {
          lastUnfilteredRowsRef.current = sortedResults;
        }

        itemsRef.current = sortedResults;
        setItems(sortedResults);
        onAdvObjConSearchResultChange(sortedResults);
        resetSortToDefault();
        if (sortedResults.length === 0) {
          onAdvObjResultsStatusChanged(PROGRESS_STATUS.noData);
          return;
        }
        onAdvObjResultsStatusChanged(PROGRESS_STATUS.onCompletion);
        onLoginRefreshPreActionClear();
      } else if (res.status === 400) {
        itemsRef.current = [];
        setItems([]);
        onAdvObjConSearchResultChange([]);
        onAdvObjResultsStatusChanged(PROGRESS_STATUS.noData);
        onLoginRefreshPreActionClear();
      } else {
        appInsightProps.statusCode = res.status;
        setErrorMessage(NO_LOGICALUNIT_FOUND);
        if (res.status === 204) {
          const columns = advObjConColumnsRef.current;
          const { data: sortedResults } = resolveAdvSearchResultRows(
            [],
            clientFiltersRef.current,
            columns,
            lastUnfilteredRowsRef.current,
            mruKeyNamesRef.current,
          );
          if (sortedResults.length > 0) {
            itemsRef.current = sortedResults;
            setItems(sortedResults);
            onAdvObjConSearchResultChange(sortedResults);
            onAdvObjResultsStatusChanged(PROGRESS_STATUS.onCompletion);
            onLoginRefreshPreActionClear();
            return;
          }
          appInsights.trackException({
            error: `${NO_LOGICALUNIT_FOUND}`,
            appInsightProps,
          });
          onAdvObjResultsStatusChanged(PROGRESS_STATUS.noData);
        } else {
          appInsights.trackException({
            error: `${IFS_SERVER_ERROR}`,
            errorMessage: res.data,
            appInsightProps,
          });
          setErrorMessage(IFS_SERVER_ERROR);
          onAdvObjResultsStatusChanged(PROGRESS_STATUS.onError);
        }
        onLoginRefreshPreActionClear();
      }
    } catch (error) {
      if (requestId !== searchRequestIdRef.current) {
        return;
      }
      console.log(error?.toJSON?.() ?? error, "err");
      if (
        isTokenRefreshRequired(
          error.response,
          tokenRefreshPreAction,
          REFRESH_TOKEN_ACTION.advanceSearchObjectsLoad,
        )
      ) {
        const updatedPreAction = getUpdatedTokenRefreshPreAction(
          tokenRefreshPreAction,
          REFRESH_TOKEN_ACTION.advanceSearchObjectsLoad,
        );
        onLoginRefreshPreActionLoad(updatedPreAction);
        try {
          await onIfsUserLogin(true, updatedPreAction);
        } catch (refreshErr) {
          console.log(refreshErr?.message ?? refreshErr, "IFS token refresh failed");
          onLoginRefreshPreActionClear();
          setErrorMessage(IFS_TOKEN_EXPIRED);
          onAdvObjResultsStatusChanged(PROGRESS_STATUS.onError);
        }
      } else if (!error?.response) {
        onLoginRefreshPreActionClear();
        const msg = error?.message ?? String(error);
        setErrorMessage(`${ERROR_DETAILS} ${msg}`);
        onExceptionMessageInvoke({ message: msg, showMessageBox: true });
        onAdvObjResultsStatusChanged(PROGRESS_STATUS.onError);
      } else if (error.response?.status === 400) {
        onLoginRefreshPreActionClear();
        itemsRef.current = [];
        setItems([]);
        onAdvObjConSearchResultChange([]);
        onAdvObjResultsStatusChanged(PROGRESS_STATUS.noData);
      } else {
        const serverResponseError = {
          response: error.response,
          statusCode: error.response?.status || TIME_OUT_STATUS_CODE,
          error: error,
          showMessageBox: true,
        };
        onLoginRefreshPreActionClear();
        setErrorMessage(ADVANCED_SEARCH_RESULTS_ERROR);
        onServerErrorMessageInvoke(serverResponseError);
        onAdvObjResultsStatusChanged(PROGRESS_STATUS.onError);
      }
    }
  }

  loadSearchResultsRef.current = loadSearchResults;

  useImperativeHandle(ref, () => ({
    executeSearch: (clause, clientFilters) =>
      loadSearchResultsRef.current?.(
        clause !== undefined ? clause : whereClauseRef.current,
        undefined,
        clientFilters ?? [],
      ),
  }));

  switch (advObjResultsStatus) {
    case PROGRESS_STATUS.onCompletion:
      return (
        <Stack
          enableScopedSelectors
          style={{ height: "100%", minHeight: 0, background: advSearchGridColors.rowBg }}
          styles={{
            root: {
              background: advSearchGridColors.rowBg,
              height: "100%",
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              borderRadius: 0,
            },
          }}
        >
          <Stack.Item
            grow
            className={classNames.wrapper}
            styles={{
              root: {
                minHeight: 0,
                overflow: "hidden",
                flex: "1 1 auto",
                display: "flex",
                flexDirection: "column",
                height: "100%",
                background: advSearchGridColors.rowBg,
              },
            }}
          >
            <div
              ref={scrollSyncRef}
              onScroll={handleScroll}
              style={{
                position: "relative",
                height: "100%",
                width: "100%",
                minHeight: 0,
                background: advSearchGridColors.rowBg,
              }}
            >
            <ScrollablePane
              key={`pane-${gridRenderKey}`}
              scrollbarVisibility={ScrollbarVisibility.auto}
              style={{ height: "100%", maxHeight: "100%", background: advSearchGridColors.rowBg }}
              styles={{
                root: {
                  height: "100%",
                  maxHeight: "100%",
                  borderRadius: 0,
                  background: advSearchGridColors.rowBg,
                  selectors: {
                    ".ms-ScrollablePane--contentContainer": {
                      overflowX: "auto",
                      overflowY: "auto",
                      background: advSearchGridColors.rowBg,
                      scrollbarWidth: "thin",
                      scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
                      "-ms-overflow-style": "auto",
                    },
                    ".ms-DetailsHeader": {
                      minWidth: tableMinWidth,
                    },
                    ".ms-ScrollablePane--contentContainer::-webkit-scrollbar": {
                      width: sbWidth,
                      height: sbHeight,
                      display: "block",
                    },
                    ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-track":
                      {
                        background: scrollbarTrack,
                      },
                    ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb":
                      {
                        background: scrollbarThumb,
                        borderRadius: sbWidth,
                        border: `2px solid ${scrollbarTrack}`,
                        minHeight: 40,
                      },
                    ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-thumb:hover":
                      {
                        background: isDarkMode ? "#9a9a9a" : "#b3b3b3",
                      },
                    ".ms-ScrollablePane--contentContainer::-webkit-scrollbar-button":
                      {
                        display: "none",
                        height: 0,
                        width: 0,
                      },
                  },
                },
              }}
            >
              <DetailsList
                key={`list-${gridRenderKey}`}
                styles={advSearchDetailListStyle}
                className={classNames.detailsList}
                items={items}
                compact={false}
                columns={columnsWithSelect}
                selectionMode={SelectionMode.none}
                getKey={getKey}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                constrainMode={ConstrainMode.unconstrained}
                isHeaderVisible={true}
                selectionPreservedOnEmptyClick={true}
                onShouldVirtualize={() => false}
                onRenderRow={(rowProps, defaultRender) => {
                  if (!rowProps) return null;
                  const { item, itemIndex } = rowProps;
                  const checked = !!(getRowId(item, itemIndex) && selectedByIdRef.current.has(getRowId(item, itemIndex)));

                  return (
                    <DetailsRow
                      {...rowProps}
                      className={`${rowProps.className ?? ""}${checked ? " is-selected" : ""}`}
                      onClick={(ev) => {
                        // If the click originated in the checkbox cell, let the checkbox handler own it.
                        const t = ev?.target;
                        const inCheck =
                          t?.closest?.('[data-automationid="DetailsRowCheck"]') ||
                          t?.closest?.(".advObjRowCheckWrapper") ||
                          t?.closest?.(".advObjSelectAllWrapper");
                        if (inCheck) return;

                        ev?.preventDefault?.();
                        toggleRowSelection(item, itemIndex, !checked);
                      }}
                    />
                  );
                }}
                ariaLabelForSelectionColumn="Toggle selection"
                ariaLabelForSelectAllCheckbox="Toggle selection for all items"
                checkButtonAriaLabel="select row"
                visibility="false"
                onColumnResize={onColumnResize}
                onRenderDetailsHeader={(headerProps, defaultRender) => {
                  return (
                    <Sticky
                      stickyPosition={StickyPositionType.Header}
                      isScrollSynced={true}
                      stickyBackgroundColor={advSearchGridColors.headerBg}
                    >
                      {defaultRender({
                        ...headerProps,
                        styles: advSearchHeaderStyle,
                        onRenderColumnHeaderTooltip: (tooltipHostProps) => (
                          <span
                            className={tooltipHostProps.hostClassName}
                            style={{
                              display: "flex",
                              position: "absolute",
                              inset: 0,
                              alignItems: "center",
                              justifyContent: "flex-start",
                              textAlign: "left",
                            }}
                          >
                            {tooltipHostProps.children}
                          </span>
                        ),
                      })}
                    </Sticky>
                  );
                }}
              />
            </ScrollablePane>
            </div>
          </Stack.Item>
        </Stack>
      );
      break;
    case PROGRESS_STATUS.onLoading:
      return (
        <div
          style={{
            position: "relative",
            height: "100%",
            minHeight: "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ width: "50px", height: "50px" }}>
            <Loader />
          </div>
        </div>
      );
      break;
    case PROGRESS_STATUS.noData:
      return (
        <>
          <div
            style={{
              ...loaderBorderStyle,
              height: "100%",
              minHeight: "240px",
              overflow: "auto",
              boxSizing: "border-box",
            }}
          >
            <div style={messageBorderStyle}>
              <IconButton
                style={{ marginLeft: "80px" }}
                iconProps={binIcon}
                title="Refresh"
                ariaLabel="Refresh"
                onClick={(ev) => {
                  loadSearchResults(
                    whereClauseRef.current,
                    undefined,
                    clientFiltersRef.current,
                  );
                }}
              />
              <div
                style={{
                  justifyContent: "center",
                  padding: "0px",
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Label style={styleTheme.HeadinglabelInformationStyle}>
                  {NO_LOGICALUNIT_FOUND_MAIN}
                </Label>
                <Label style={styleTheme.SublabelInformationStyle}>
                  {NO_LOGICALUNIT_FOUND_SUB}
                </Label>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <img
                  src={advSearchError}
                  alt="No Results"
                  style={{
                    maxWidth: "160%",
                    maxHeight: "100%",
                    width: "auto",
                    height: "auto",
                    objectFit: "contain",
                    marginTop: "20px",
                  }}
                />
              </div>
            </div>
          </div>
        </>
      );
    case PROGRESS_STATUS.onError:
      return (
        <>
          <div
            style={{
              ...loaderBorderStyle,
              height: "100%",
              minHeight: "240px",
              overflow: "auto",
              boxSizing: "border-box",
            }}
          >
            <div style={messageBorderStyle}>
              <IconButton
                style={{ marginLeft: "80px" }}
                iconProps={binIcon}
                title="Refresh"
                ariaLabel="Refresh"
                onClick={(ev) => {
                  loadSearchResults(
                    whereClauseRef.current,
                    undefined,
                    clientFiltersRef.current,
                  );
                }}
              />

              {ifsError ? (
                <div>
                  <Label
                    style={{
                      ...styleTheme.LabelErrorMessageStyle,
                      fontSize: "12px",
                      fontWeight: "normal",
                    }}
                  >
                    {ifsError}
                  </Label>
                </div>
              ) : (
                <Label style={styleTheme.LabelErrorMessageStyle}>
                  {errorMessage}
                </Label>
              )}
            </div>
          </div>
        </>
      );
    default:
      return <></>;
  }
});

export default AdvSearchObjectResults;
